import { createContext } from '@shared/utils/createContext';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Answer, Answers, CreateSurveyArgs, StepDefinition, SurveyContextType } from './Survey.types';
import { useSurveyTelemetry } from './Survey.telemetry';

const [SurveyProviderInner, useSurveyBase] = createContext<SurveyContextType>({
  name: 'SurveyContext',
});

export function useSurvey() {
  return useSurveyBase();
}

export const SurveyProvider: React.FC<CreateSurveyArgs> = ({ config, onComplete, telemetryConfig, children }) => {
  const telemetry = useSurveyTelemetry();

  const [currentStep, setCurrentStep] = useState(config.initialStep);
  const [answers, setAnswers] = useState<Answers>(config.initialValues ?? {});

  const stepsMap = useMemo(() => {
    return config.steps.reduce<Record<string, StepDefinition>>((acc, step) => {
      acc[step.name] = step;
      return acc;
    }, {});
  }, [config.steps]);

  const stepConfig = stepsMap[currentStep];

  useEffect(() => {
    telemetry.surveyPageViewed.enter(config.name, currentStep, answers, telemetryConfig.category, telemetryConfig.pagePrefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const updateAnswer = (id: string, value: Answer) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const hasNextStep = useMemo(() => {
    const stepConfig = stepsMap[currentStep];
    return !!(stepConfig.next || stepConfig.components.some(component => component.type === 'radio' && component.options.some(option => option.next)));
  }, [currentStep, stepsMap]);

  /**
   * Function to go to the next step in the survey.
   * It checks the current step's configuration for the next step or fallback to the default next step.
   * If no next step is defined, it calls the onComplete callback with the current answers.
   * It also handles the case where the next step is determined by the selected options in radio components.
   */
  const goNext = useCallback(() => {
    const stepConfig = stepsMap[currentStep];

    const optionNextStep = stepConfig.components.flatMap(component => {
      if (component.type === 'radio') {
        const answer = answers[component.id];
        const selected = Array.isArray(answer) ? answer : [answer];
        return selected.map(sel => component.options.find(opt => opt.value === sel)?.next).filter(Boolean) as string[];
      }
      return [];
    })[0];

    const fallbackNext = typeof stepConfig.next === 'string' ? stepConfig.next : undefined;

    const nextStep = optionNextStep || fallbackNext;

    if (nextStep) {
      telemetry.surveyNextStepClicked(config.name, currentStep, nextStep, answers, telemetryConfig.category, telemetryConfig.pagePrefix);
      setCurrentStep(nextStep);
    } else if (onComplete) {
      telemetry.surveyCompleted(config.name, currentStep, answers, telemetryConfig.category, telemetryConfig.pagePrefix);
      onComplete(answers);
    }
  }, [answers, config.name, currentStep, onComplete, stepsMap, telemetry, telemetryConfig.category, telemetryConfig.pagePrefix]);

  const goBack = useCallback(() => {
    if (stepConfig.back) {
      setCurrentStep(stepConfig.back as string);
    }
  }, [stepConfig.back]);

  return (
    <SurveyProviderInner
      value={{
        currentStep,
        setStep: setCurrentStep,
        goNext,
        goBack,
        answers,
        updateAnswer,
        config,
        stepConfig,
        hasNextStep,
      }}
    >
      {children}
    </SurveyProviderInner>
  );
};
